import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown } from 'react-feather';
import { DillButton } from './DillButton';
import { Tooltip as ReactTooltip } from 'react-tooltip';

interface Option {
  value: string;
  label: string;
  icon?: React.ReactNode;
  extraData?: any;
  isDisabled?: boolean;
  tooltipText?: string;
}

interface DillDropdownButtonProps {
  options: Option[];
  onSelect: (option: Option) => void;
  label?: string;
  icon?: React.ReactNode;
  showChevron?: boolean;
  variant?:
    | 'primary'
    | 'secondary'
    | 'danger'
    | 'ghost'
    | 'icon-default'
    | 'icon-red'
    | 'icon-blue';
  isDisabled?: boolean;
  isLoading?: boolean;
  contentWidth?: number;
  height?: number | string;
  width?: number | string;
}

export const DillDropdownButton = ({
  options,
  onSelect,
  label,
  icon,
  showChevron = false,
  variant = 'primary',
  isDisabled = false,
  isLoading = false,
  contentWidth = 200,
  height,
  width,
}: DillDropdownButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let animationFrameId: number;

    const updatePosition = () => {
      if (!isOpen || !buttonRef.current) return;

      const rect = buttonRef.current.getBoundingClientRect();

      const isInViewport =
        rect.top >= 0 &&
        rect.bottom <= window.innerHeight &&
        rect.left >= 0 &&
        rect.right <= window.innerWidth;

      if (isInViewport) {
        const dropdownHeight = dropdownRef.current?.offsetHeight || 100;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const shouldOpenUpward = spaceBelow < dropdownHeight + 20 && spaceAbove > spaceBelow;

        setDropdownPosition({
          top: shouldOpenUpward ? rect.top - dropdownHeight - 5 : rect.bottom + 6,
          left: rect.right - contentWidth,
        });

        animationFrameId = requestAnimationFrame(updatePosition);
      } else {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      animationFrameId = requestAnimationFrame(updatePosition);
    }

    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [isOpen, contentWidth]);

  const handleOptionClick = (option: Option) => {
    onSelect(option);
    setIsOpen(false);
  };

  return (
    <>
      <div ref={buttonRef}>
        <DillButton
          label={label}
          onClick={() => setIsOpen((prev) => !prev)}
          variant={variant}
          icon={icon}
          rightIcon={
            showChevron && label ? (
              <span
                className="transition-transform duration-200"
                style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', display: 'flex' }}>
                <ChevronDown size={14} />
              </span>
            ) : undefined
          }
          isDisabled={isDisabled}
          isLoading={isLoading}
          height={height}
          width={width}
        />
      </div>
      {isOpen &&
        ReactDOM.createPortal(
          <>
            <div
              style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
              onClick={() => setIsOpen(false)}
            />
            <div
              ref={dropdownRef}
              style={{
                position: 'fixed',
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
                width: contentWidth,
                zIndex: 9999,
              }}
              className="border border-gray-200 bg-white py-1.5 rounded-lg shadow-xl">
              {options.map((option, i) => {
                const tooltipId = `dill-dropdown-option-${option.value}-${i}`;
                return (
                  <div key={`${option.value}_${i}`}>
                    <div
                      data-tooltip-id={
                        option.isDisabled && option.tooltipText ? tooltipId : undefined
                      }
                      onClick={() => !option.isDisabled && handleOptionClick(option)}
                      className={`flex items-center gap-2 px-3 py-2 text-xs ${
                        option.isDisabled
                          ? 'text-gray-400 bg-gray-50 cursor-not-allowed'
                          : 'cursor-pointer text-gray-700 hover:bg-blue-50 hover:text-blue-600'
                      }`}>
                      {option.icon && (
                        <span className={option.isDisabled ? 'opacity-40' : ''}>{option.icon}</span>
                      )}
                      <span className="truncate">{option.label}</span>
                    </div>
                    {option.isDisabled && option.tooltipText && (
                      <ReactTooltip
                        id={tooltipId}
                        place="left"
                        variant="dark"
                        opacity={'100%'}
                        style={{
                          background: '#334155',
                          zIndex: 10000,
                          borderRadius: '8px',
                          fontWeight: '600',
                          maxWidth: '220px',
                          fontSize: '12px',
                        }}>
                        <span>{option.tooltipText}</span>
                      </ReactTooltip>
                    )}
                  </div>
                );
              })}
            </div>
          </>,
          document.body
        )}
    </>
  );
};
